import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "step",
    "welcomeScreen",
    "introSection",
    "questionsFlow",
    "question",
    "progressText",
    "progressBar",
    "questionIndicator",
    "prevButton",
    "nextButton",
    "submitButton",
  ]
  static values = {
    currentStep: { type: Number, default: 0 },
    currentQuestion: { type: Number, default: 0 },
  }

  connect() {
    if (this.hasQuestionTarget) {
      if (this.hasWelcomeScreenTarget && this.hasQuestionsFlowTarget) {
        this.questionsFlowTarget.classList.add("hidden")
        this.welcomeScreenTarget.classList.remove("hidden")
        if (this.hasIntroSectionTarget) this.introSectionTarget.classList.remove("hidden")
        return
      }

      this.showQuestion(this.currentQuestionValue)
      this.updateProgress()
      this.updateNavigation()
      return
    }

    this.showStep(this.currentStepValue)
  }

  startOnboarding(event) {
    if (event) event.preventDefault()

    if (this.hasQuestionTarget && this.hasQuestionsFlowTarget) {
      this.welcomeScreenTarget.classList.add("hidden")
      if (this.hasIntroSectionTarget) this.introSectionTarget.classList.add("hidden")
      this.questionsFlowTarget.classList.remove("hidden")
      this.showQuestion(this.currentQuestionValue)
      this.updateProgress()
      this.updateNavigation()
      return
    }

    if (this.hasWelcomeScreenTarget && this.hasStepTarget) {
      this.welcomeScreenTarget.classList.add("hidden")
      this.currentStepValue = 1
      this.showStep(this.currentStepValue)
    }
  }

  nextStep(event) {
    if (event) event.preventDefault()
    this.currentStepValue++
    this.showStep(this.currentStepValue)
  }

  prevStep(event) {
    if (event) event.preventDefault()
    this.currentStepValue--
    this.showStep(this.currentStepValue)
  }

  showStep(index) {
    this.stepTargets.forEach((element, i) => {
      element.classList.toggle("hidden", (i + 1) !== index)
    })
  }

  nextQuestion(event) {
    if (event) event.preventDefault()
    if (!this.currentQuestionAnswered()) {
      this.highlightCurrentQuestion()
      return
    }

    if (this.currentQuestionValue < this.questionTargets.length - 1) {
      this.currentQuestionValue += 1
      this.showQuestion(this.currentQuestionValue)
      this.updateNavigation()
    }
  }

  prevQuestion(event) {
    if (event) event.preventDefault()
    if (this.currentQuestionValue > 0) {
      this.currentQuestionValue -= 1
      this.showQuestion(this.currentQuestionValue)
      this.updateNavigation()
    }
  }

  updateProgress() {
    if (!this.hasQuestionTarget) return

    const total = this.questionTargets.length
    const answered = this.questionTargets.filter((question) => {
      return question.querySelector("input[type='radio']:checked")
    }).length
    const currentPosition = total === 0 ? 0 : Math.min(this.currentQuestionValue + 1, total)
    const displayProgress = Math.max(answered, currentPosition)
    const percentage = total === 0 ? 0 : Math.round((displayProgress / total) * 100)

    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = `${displayProgress}/${total}`
    }

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percentage}%`
    }
  }

  optionSelected(event) {
    this.updateProgress()

    const questionElement = event?.target?.closest("[data-participation-target='question']")
    const questionIndex = this.questionTargets.indexOf(questionElement)
    if (questionIndex < 0) return

    this.currentQuestionValue = questionIndex
    const lastQuestion = this.currentQuestionValue === this.questionTargets.length - 1

    if (lastQuestion) {
      this.updateNavigation()
      return
    }

    window.setTimeout(() => {
      this.currentQuestionValue += 1
      this.showQuestion(this.currentQuestionValue)
      this.updateNavigation()
    }, 120)
  }

  showQuestion(index) {
    this.questionTargets.forEach((question, i) => {
      question.classList.toggle("hidden", i !== index)
    })

    if (this.hasQuestionIndicatorTarget) {
      this.questionIndicatorTarget.textContent = `Pergunta ${index + 1} de ${this.questionTargets.length}`
    }

    this.updateProgress()
  }

  updateNavigation() {
    if (!this.hasQuestionTarget) return

    const firstQuestion = this.currentQuestionValue === 0
    const lastQuestion = this.currentQuestionValue === this.questionTargets.length - 1
    const currentAnswered = this.currentQuestionAnswered()

    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.disabled = firstQuestion
      this.prevButtonTarget.classList.toggle("opacity-50", firstQuestion)
      this.prevButtonTarget.classList.toggle("cursor-not-allowed", firstQuestion)
    }

    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.classList.toggle("hidden", lastQuestion)
      this.nextButtonTarget.classList.toggle("inline-flex", !lastQuestion)
      this.nextButtonTarget.disabled = lastQuestion
    }

    if (this.hasSubmitButtonTarget) {
      const canSubmit = lastQuestion && currentAnswered
      this.submitButtonTarget.classList.toggle("hidden", !canSubmit)
      this.submitButtonTarget.disabled = !canSubmit
    }
  }

  currentQuestionAnswered() {
    const currentQuestion = this.questionTargets[this.currentQuestionValue]
    return Boolean(currentQuestion?.querySelector("input[type='radio']:checked"))
  }

  highlightCurrentQuestion() {
    const currentQuestion = this.questionTargets[this.currentQuestionValue]
    if (!currentQuestion) return

    currentQuestion.classList.add("ring-2", "ring-red-300")
    setTimeout(() => {
      currentQuestion.classList.remove("ring-2", "ring-red-300")
    }, 600)
  }
}
